import { Dialog, DialogBackdrop, DialogPanel, DialogTitle } from '@headlessui/react';
import { ExclamationTriangleIcon, QuestionMarkCircleIcon } from '@heroicons/react/24/outline';
import CT from '@marketplace-ai/tags';
import { useSelector } from '@xstate/react';
import { useConfirmationView } from '~/app';
import { Button } from '~/components/buttons/Button/Button';
import type { ConfirmationTone } from '~/features/confirmation/types';

const TAGS = CT.confirmationDialog;

/**
 * [MCV-1572] Presentation per tone. `destructive` is the historical (and
 * default) look; `neutral` exists because not every confirmation is a warning
 * — a data export just wants a deliberate second step. Styling a benign action
 * in danger colours misleads the user, and it also cheapens the red treatment
 * where it genuinely means "this cannot be undone".
 */
const TONE_STYLES: Record<
  ConfirmationTone,
  {
    Icon: typeof ExclamationTriangleIcon;
    iconWrapper: string;
    icon: string;
    confirmColor: 'error' | 'primary';
  }
> = {
  destructive: {
    Icon: ExclamationTriangleIcon,
    iconWrapper: 'bg-error-t-900',
    icon: 'text-error-50',
    confirmColor: 'error',
  },
  neutral: {
    Icon: QuestionMarkCircleIcon,
    iconWrapper: 'bg-primary-t-900',
    icon: 'text-primary-50',
    confirmColor: 'primary',
  },
};

export const ConfirmationDialog = () => {
  const confirmationRef = useConfirmationView();
  const show = useSelector(confirmationRef, (state) => state.matches('$_SHOWING'));
  const { title, message, onConfirm, onCancel, confirmLabel, tone } = useSelector(
    confirmationRef,
    (state) => state.context
  );
  const toneStyles = TONE_STYLES[tone] ?? TONE_STYLES.destructive;

  const handleConfirm = () => {
    if (typeof onConfirm === 'function') {
      onConfirm();
    } else {
      console.error('[ConfirmationDialog] onConfirm is not a function:', typeof onConfirm);
    }
  };

  const handleCancel = () => {
    if (typeof onCancel === 'function') {
      onCancel();
    }
  };

  return (
    <Dialog open={show} onClose={handleCancel} className="relative z-50">
      <DialogBackdrop
        transition
        className="fixed inset-0 bg-grey-100/60 backdrop-blur-sm transition-opacity data-closed:opacity-0 data-enter:duration-300 data-enter:ease-out data-leave:duration-200 data-leave:ease-in"
      />

      <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
        <div className="flex min-h-full items-center justify-center p-4">
          <DialogPanel
            transition
            className="relative w-full max-w-md transform rounded-xl bg-white p-6 shadow-xl transition-all data-closed:translate-y-4 data-closed:opacity-0 data-enter:duration-300 data-enter:ease-out data-leave:duration-200 data-leave:ease-in data-closed:sm:translate-y-0 data-closed:sm:scale-95"
          >
            <div className="flex items-start gap-4">
              <div
                data-cy={`${TAGS.title}-icon-${tone}`}
                className={`flex size-10 shrink-0 items-center justify-center rounded-full ${toneStyles.iconWrapper}`}
              >
                <toneStyles.Icon className={`size-5 ${toneStyles.icon}`} aria-hidden="true" />
              </div>
              <div className="flex-1">
                <DialogTitle data-cy={TAGS.title} className="text-lg font-semibold text-grey-100">
                  {title}
                </DialogTitle>
                <p data-cy={TAGS.message} className="mt-2 text-sm text-grey-400">
                  {message}
                </p>
              </div>
            </div>
            <div className="mt-6 flex gap-3 justify-end">
              <Button
                cyTag={TAGS.cancelBtn}
                type="button"
                colorVariant="secondary"
                onClick={handleCancel}
              >
                Cancel
              </Button>
              <Button
                cyTag={TAGS.confirmBtn}
                type="button"
                colorVariant={toneStyles.confirmColor}
                onClick={handleConfirm}
              >
                {confirmLabel ?? 'Delete'}
              </Button>
            </div>
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  );
};
